import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Paper,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { toast } from 'react-toastify'

// Define field mapping information for inventory
const exportFields = {
  name: { name: 'Name', description: 'Inventory item name', defaultSelected: true },
  description: {
    name: 'Description',
    description: 'Inventory item description',
    defaultSelected: true
  },
  upc: { name: 'UPC', description: 'Universal Product Code', defaultSelected: false },
  stock_available: {
    name: 'Stock Available',
    description: 'Current available stock',
    defaultSelected: true
  },
  stock_total: { name: 'Stock Total', description: 'Total stock quantity', defaultSelected: true },
  stock_unit: {
    name: 'Stock Unit',
    description: 'Unit of measurement for stock',
    defaultSelected: false
  },
  category: { name: 'Category', description: 'Inventory category', defaultSelected: true },
  location: { name: 'Location', description: 'Inventory location', defaultSelected: true },
  group: { name: 'Group', description: 'Inventory group', defaultSelected: false },
  tags: {
    name: 'Tags',
    description: 'Inventory tags (Free, Issue, Purchase, etc.)',
    defaultSelected: true
  },
  manufacturer: { name: 'Manufacturer', description: 'Manufacturer name', defaultSelected: false },
  purchase_cost: { name: 'Purchase Cost', description: 'Purchase cost', defaultSelected: false },
  purchase_date: { name: 'Purchase Date', description: 'Date of purchase', defaultSelected: false },
  warranty_expiration: {
    name: 'Warranty Expiration',
    description: 'Warranty expiration date',
    defaultSelected: false
  }
  // created_at: { name: 'Created Date', description: 'Item creation date', defaultSelected: false },
  // updated_at: { name: 'Last Updated', description: 'Last update date', defaultSelected: false }
}

// Mapping for tag values
const tagMapping = {
  0: 'Free',
  1: 'Issue',
  2: 'Purchase',
  3: 'Exhaustible',
  4: 'NonExhaustible',
  5: 'InLabOnly',
  6: 'HighValue'
}

const InventoryExport = ({ inventories, categories, locations, groups }) => {
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
  const [selectedFields, setSelectedFields] = useState(
    Object.fromEntries(
      Object.entries(exportFields).map(([key, field]) => [key, field.defaultSelected])
    )
  )
  const [isExporting, setIsExporting] = useState(false)

  // Handler for opening the export dialog
  const handleOpenExportDialog = () => {
    setIsExportDialogOpen(true)
  }

  // Handler for closing the export dialog
  const handleCloseExportDialog = () => {
    setIsExportDialogOpen(false)
  }

  // Toggle selection of a field
  const handleFieldToggle = (field) => {
    setSelectedFields((prev) => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  // Select all fields
  const handleSelectAll = () => {
    setSelectedFields(Object.fromEntries(Object.keys(exportFields).map((key) => [key, true])))
  }

  // Deselect all fields
  const handleDeselectAll = () => {
    setSelectedFields(Object.fromEntries(Object.keys(exportFields).map((key) => [key, false])))
  }

  // Convert datetime string to readable format
  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Get category name - handles both ID (number) and name (string)
  const getCategoryName = (categoryValue) => {
    if (!categoryValue) return ''

    // If it's already a string (name), check if it exists in categories
    if (typeof categoryValue === 'string') {
      const category = categories?.find((c) => c.name === categoryValue)
      return category ? category.name : categoryValue
    }

    // If it's a number (ID), look it up
    const category = categories?.find((c) => c.id === categoryValue)
    return category ? category.name : ''
  }

  // Get location name - handles both ID (number) and name (string)
  const getLocationName = (locationValue) => {
    if (!locationValue) return ''

    // If it's already a string (name), check if it exists in locations
    if (typeof locationValue === 'string') {
      const location = locations?.find((l) => l.name === locationValue)
      return location ? location.name : locationValue
    }

    // If it's a number (ID), look it up
    const location = locations?.find((l) => l.id === locationValue)
    return location ? location.name : ''
  }

  // Get group name by ID
  const getGroupName = (groupId) => {
    if (!groupId) return ''

    if (typeof groupId === 'string') {
      const group = groups?.find((g) => g.name === groupId)
      return group ? group.name : groupId
    }

    // If it's a number (ID), look it up
    const group = groups?.find((g) => g.id === groupId)
    return group ? group.name : ''
  }

  // Format tags array to readable string
  const formatTags = (tagsArray) => {
    if (!tagsArray || !Array.isArray(tagsArray)) return ''
    return tagsArray.map((tag) => tagMapping[tag] || tag).join(', ')
  }

  // Format inventory data for export
  const formatInventoryDataForExport = (inventory) => {
    const formattedData = {}

    if (selectedFields.name) formattedData.Name = inventory.name || ''
    if (selectedFields.description) formattedData.Description = inventory.description || ''
    if (selectedFields.upc) formattedData.UPC = inventory.upc || ''
    if (selectedFields.stock_available)
      formattedData['Stock Available'] = inventory.stock_available || ''
    if (selectedFields.stock_total) formattedData['Stock Total'] = inventory.stock_total || ''
    if (selectedFields.stock_unit) formattedData['Stock Unit'] = inventory.stock_unit || ''

    if (selectedFields.category) {
      formattedData.Category = getCategoryName(inventory.category)
    }

    if (selectedFields.location) {
      formattedData.Location = getLocationName(inventory.location)
    }

    if (selectedFields.group) {
      formattedData.Group = getGroupName(inventory.group)
    }

    if (selectedFields.tags) {
      formattedData.Tags = formatTags(inventory.tags)
    }

    if (selectedFields.manufacturer) formattedData.Manufacturer = inventory.manufacturer || ''
    if (selectedFields.purchase_cost) formattedData['Purchase Cost'] = inventory.purchase_cost || ''
    if (selectedFields.purchase_date)
      formattedData['Purchase Date'] = formatDate(inventory.purchase_date)
    if (selectedFields.warranty_expiration)
      formattedData['Warranty Expiration'] = formatDate(inventory.warranty_expiration)
    // if (selectedFields.created_at) formattedData['Created Date'] = formatDate(inventory.created_at);
    // if (selectedFields.updated_at) formattedData['Last Updated'] = formatDate(inventory.updated_at);

    return formattedData
  }

  // Handle the export process
  const handleExport = () => {
    if (Object.values(selectedFields).every((val) => !val)) {
      toast.error('Please select at least one field to export')
      return
    }

    setIsExporting(true)

    try {
      // Format all inventories
      const formattedInventories = inventories.map(formatInventoryDataForExport)

      if (formattedInventories.length === 0) {
        toast.warning('No inventory items to export')
        setIsExporting(false)
        return
      }

      // Get headers from the first inventory item
      const headers = Object.keys(formattedInventories[0])

      // Create CSV content
      let csvContent = headers.join(',') + '\n'

      // Add data rows
      formattedInventories.forEach((inventory) => {
        const row = headers.map((header) => {
          // Handle fields that might contain commas or quotes
          const cell = String(inventory[header] || '')
          if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
            return `"${cell.replace(/"/g, '""')}"`
          }
          return cell
        })
        csvContent += row.join(',') + '\n'
      })

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute(
        'download',
        `inventory_export_${new Date().toISOString().split('T')[0]}.csv`
      )
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      setIsExporting(false)
      setIsExportDialogOpen(false)
      toast.success('Inventory exported successfully')
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export inventory')
      setIsExporting(false)
    }
  }

  // Count selected fields
  const selectedFieldCount = Object.values(selectedFields).filter(Boolean).length

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleOpenExportDialog}
        startIcon={<FileDownloadIcon />}
        sx={{
          flexDirection: 'row',
          padding: '12px 16px',
          borderRadius: '12px',
          backgroundColor: '#FAFAFA',
          color: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #E6E6E6',
          gap: '8px',
          textTransform: 'none',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            backgroundColor: '#FAFAFA',
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            borderColor: '#E6E6E6'
          }
        }}
      >
        <Typography variant="subtitle1">Export</Typography>
      </Button>

      <Dialog
        open={isExportDialogOpen}
        onClose={handleCloseExportDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={500}>
              Export Inventory to CSV
            </Typography>
            <Chip label={`${selectedFieldCount} fields selected`} color="primary" size="small" />
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Select the fields you want to include in the export:
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelectAll}
              sx={{ textTransform: 'none' }}
            >
              Select All
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleDeselectAll}
              sx={{ textTransform: 'none' }}
            >
              Deselect All
            </Button>
          </Box>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Inventory Fields
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
              {Object.entries(exportFields).map(([key, field]) => (
                <FormGroup key={key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedFields[key] || false}
                        onChange={() => handleFieldToggle(key)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2">{field.name}</Typography>
                        <Tooltip title={field.description} arrow>
                          <IconButton size="small">
                            <HelpOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                </FormGroup>
              ))}
            </Box>
          </Paper>

          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {inventories?.length || 0} inventory item{inventories?.length !== 1 ? 's' : ''} will
              be exported
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button onClick={handleCloseExportDialog} color="inherit" disabled={isExporting}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            color="primary"
            disabled={isExporting || selectedFieldCount === 0}
            startIcon={
              isExporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />
            }
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default InventoryExport
